'use client';

import { useEffect, useMemo, useState } from 'react';
import type { Product } from '@/lib/products';
import { useTranslation } from '@/context/LanguageContext';
import { trackProductListViewed } from '@/lib/analytics';
import ProductCard from '@/components/ProductCard';
import styles from './page.module.scss';

export default function ShopContent() {
  const { t, currency } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/products')
      .then((r) => r.json())
      .then((data: Product[]) => {
        if (cancelled) return;
        setProducts(data);
      })
      .catch(() => {
        if (cancelled) return;
        setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const translatedProducts = useMemo(
    () =>
      products.map((product) => {
        const tp = t.products[product.slug as keyof typeof t.products];
        if (!tp) return product;
        return {
          ...product,
          name: tp.name,
          shortName: tp.shortName,
          origin: tp.origin,
          roast: tp.roast,
          flavor: tp.flavor,
          variety: tp.variety,
          process: tp.process,
          description: tp.description,
          guide: { ...product.guide, ...tp.guide },
          guideRichText: (tp as { guideRichText?: string }).guideRichText ?? product.guideRichText,
        };
      }),
    [products, t]
  );

  useEffect(() => {
    if (translatedProducts.length === 0) return;
    trackProductListViewed(
      translatedProducts.map((p) => ({
        id: p.id,
        name: p.name,
        price: p.variants[0]?.price ?? 0,
        currency,
      }))
    );
  }, [translatedProducts, currency]);

  return (
    <section className={styles.shop}>
      <div className="container">
        <div className={styles.intro}>
          <h1 className={styles.title}>{t.shop.title}</h1>
          <p className={styles.subtitle}>
            {t.shop.subtitle}
          </p>
        </div>
        <div className={styles.grid}>
          {translatedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
